/* eslint-disable no-useless-escape */
import axios, { type AxiosProgressEvent, type AxiosRequestConfig, type AxiosError, AxiosResponse, RawAxiosRequestHeaders } from 'axios';
import { isNil, isEmpty } from 'lodash-es';
import { ApiResponse } from './api-response';
import { ApiResponseCode } from './api-response-code';
import { encodeQueryString } from './api-utils';

const DEFAULT_HEADERS: RawAxiosRequestHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

export const SKIP_AUTH_HEADER = 'x-skip-auth';

// Type guard to check if an error is an AxiosError
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isAxiosError<T = unknown, D = unknown>(error: any): error is AxiosError<T, D> {
  return error.isAxiosError === true;
}

export abstract class AbstractBaseClient {
  public baseUrl = 'unset';
  public token = '';
  public skipAuth = true;

  public apiPath: string | undefined = undefined;

  constructor(baseUrl: string, skipAuth: boolean = true) {
    this.baseUrl = baseUrl;
    this.skipAuth = skipAuth;
  }

  public abstract setBaseUrl(env: string, tenant: string): void;

  public setBaseUrlOverride(devApiUrl: string) {
    this.baseUrl = devApiUrl;
  }

  public setApiPath(path: string) {
    this.apiPath = path;
  }

  public setToken(token: string) {
    this.token = token;
  }

  public getHeaders(multipart = false): RawAxiosRequestHeaders | undefined {
    const defaultHeaders = { ...DEFAULT_HEADERS };

    if (multipart) {
      return undefined;
    }

    if (!this.skipAuth && !isEmpty(this.token)) {
      defaultHeaders.Authorization = `Bearer ${this.token}`;
    }

    if (this.skipAuth) {
      defaultHeaders[SKIP_AUTH_HEADER] = 'true';
    }

    return defaultHeaders;
  }

  public async post<T>(uri: string, data: unknown, parameters: Record<string, unknown> = {}) {
    if (Object.keys(parameters).length > 0) {
      uri = `${uri}?${encodeQueryString(parameters)}`;
    }
    try {
      const config: AxiosRequestConfig = {
        headers: this.getHeaders(),
        withCredentials: false,
        baseURL: this.baseUrl + (this.apiPath ? `${this.apiPath}` : ''),
      };

      const response = await axios.post<T>(uri, data, config);

      return ApiResponse.responseWithPayload(response.data, response.status);
    } catch (error) {
      if (isAxiosError<T>(error)) {
        if (error && error.response) {
          return ApiResponse.responseWithCodeAndMessage<T>(this.getErrorResponse(error.response));
        }
      }
      return ApiResponse.responseWithCode<T>(ApiResponseCode.UNKNOWN_ERROR, 0);
    }
  }

  public async patch<T>(uri: string, data: unknown, parameters: Record<string, unknown> = {}) {
    if (Object.keys(parameters).length > 0) {
      uri = `${uri}?${encodeQueryString(parameters)}`;
    }
    try {
      const config: AxiosRequestConfig = {
        headers: this.getHeaders(),
        withCredentials: false,
        baseURL: this.baseUrl + (this.apiPath ? `${this.apiPath}` : ''),
      };

      const response = await axios.patch<T>(uri, data, config);

      return ApiResponse.responseWithPayload(response.data, response.status);
    } catch (error) {
      if (isAxiosError<T>(error)) {
        if (error && error.response) {
          return ApiResponse.responseWithCodeAndMessage<T>(this.getErrorResponse(error.response));
        }
      }
      return ApiResponse.responseWithCode<T>(ApiResponseCode.UNKNOWN_ERROR, 0);
    }
  }

  public async put<T>(uri: string, data: unknown, parameters: Record<string, unknown> = {}) {
    if (Object.keys(parameters).length > 0) {
      uri = `${uri}?${encodeQueryString(parameters)}`;
    }
    try {
      const config: AxiosRequestConfig = {
        headers: this.getHeaders(),
        withCredentials: false,
        baseURL: this.baseUrl + (this.apiPath ? `${this.apiPath}` : ''),
      };

      const response = await axios.put<T>(uri, data, config);

      return ApiResponse.responseWithPayload(response.data, response.status);
    } catch (error) {
      if (isAxiosError<T>(error)) {
        if (error && error.response) {
          return ApiResponse.responseWithCodeAndMessage<T>(this.getErrorResponse(error.response));
        }
      }
      return ApiResponse.responseWithCode<T>(ApiResponseCode.UNKNOWN_ERROR, 0);
    }
  }

  public async delete<T>(uri: string) {
    try {
      const config: AxiosRequestConfig = {
        headers: this.getHeaders(),
        withCredentials: false,
        baseURL: this.baseUrl + (this.apiPath ? `${this.apiPath}` : ''),
      };

      const response = await axios.delete<T>(uri, config);

      return ApiResponse.responseWithPayload(response.data, response.status);
    } catch (error) {
      if (isAxiosError<T>(error)) {
        if (error && error.response) {
          return ApiResponse.responseWithCodeAndMessage<T>(this.getErrorResponse(error.response));
        }
      }
      return ApiResponse.responseWithCode<T>(ApiResponseCode.UNKNOWN_ERROR, 0);
    }
  }

  public async get<T>(uri: string, data: Record<string, unknown> = {}) {
    if (Object.keys(data).length > 0) {
      uri = `${uri}?${encodeQueryString(data)}`;
    }

    try {
      const response = await axios({
        method: 'get',
        url: uri,
        baseURL: this.baseUrl + (this.apiPath ? `${this.apiPath}` : ''),
        withCredentials: false,
        headers: this.getHeaders(),
      });
      return new ApiResponse<T>({
        payload: response.data as T,
        responseCode: ApiResponseCode.SUCCESS,
        httpResponseCode: response.status,
      });
    } catch (error) {
      if (isAxiosError<T>(error)) {
        if (error && error.response) {
          return ApiResponse.responseWithCodeAndMessage<T>(this.getErrorResponse(error.response));
        }
      }
      return ApiResponse.responseWithCode<T>(ApiResponseCode.UNKNOWN_ERROR, 0);
    }
  }

  public postWithoutCredentials<T>(uri: string, data: unknown, parameters: Record<string, unknown> = {}) {
    if (Object.keys(parameters).length > 0) {
      uri = `${uri}?${encodeQueryString(parameters)}`;
    }

    return axios
      .post<T>(uri, data, {
        withCredentials: false,
      })
      .then((res: AxiosResponse<T, unknown>) => res.data);
  }

  public getRaw<T>(uri: string, data = {}) {
    if (Object.keys(data).length > 0) {
      uri = `${uri}?${encodeQueryString(data)}`;
    }

    return axios
      .get<T>(uri, {
        headers: this.getHeaders(),
        withCredentials: false,
      })
      .then((res: AxiosResponse<T, unknown>) => res);
  }

  public async uploadFiles<T>(uri: string, formData: FormData, onUploadProgress: (progressEvent: AxiosProgressEvent) => void) {
    try {
      const response = await axios.post<T>(uri, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${this.token}`,
        },
        baseURL: this.baseUrl + (this.apiPath ? `${this.apiPath}` : ''),
        onUploadProgress,
      });
      return new ApiResponse<T>({
        payload: response.data as T,
        responseCode: ApiResponseCode.SUCCESS,
        httpResponseCode: response.status,
      });
    } catch (error) {
      if (isAxiosError<T>(error)) {
        if (error && error.response) {
          return ApiResponse.responseWithCodeAndMessage<T>(this.getErrorResponse(error.response));
        }
      }
      return ApiResponse.responseWithCode<T>(ApiResponseCode.UNKNOWN_ERROR, 0);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getErrorResponse(axiosResponse: AxiosResponse<any, any>): {
    message: string;
    code: ApiResponseCode;
    httpResponseCode?: number | undefined;
  } {
    return {
      message: !isNil(axiosResponse.data.message) && !isEmpty(axiosResponse.data.message) ? axiosResponse.data.message : 'Unknown error',
      code:
        !isNil(axiosResponse.data.statusCode) && !isEmpty(axiosResponse.data.statusCode)
          ? (axiosResponse.data.statusCode as ApiResponseCode)
          : ApiResponseCode.UNKNOWN_ERROR,
      httpResponseCode: axiosResponse?.status,
    };
  }
}
